import { Modal } from '@/client/components/shared/Modal';
import {
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  Alert,
  AlertIcon,
  Box,
  useToast,
  Switch,
  Tooltip,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect } from 'react';
import { MdDelete, MdAdd } from 'react-icons/md';
import {
  useChatbotLinks,
  useUpdateChatbotLinks,
  useChatbots,
} from '@/lib/sdk/chatbot/queries';
import type { WorkflowLinks, Chatbot, WorkflowLinkValue } from '@/lib/sdk/chatbot/types';
import { getTargetFromLink, getAttributeMappingFromLink, getShowSummaryFromLink, getShowPreCompleteSummaryFromLink } from '@/lib/sdk/chatbot/types';
import { AxiosError } from 'axios';
import { MdEdit } from 'react-icons/md';
import { ValueRefPicker } from '@/modules/workflows/components/ValueRefPicker';
import { buildValueRefTasks, type PanelTask } from '@/modules/workflows/components/valueRefTasks';
import { useFieldDefinitionsForDatasets } from '@/lib/sdk/master-data-field-definitions/queries';

interface LinksManagerProps {
  isOpen: boolean;
  onClose: () => void;
  chatbotCode?: string;
  chatbotTitle: string;
  orgId: string;
  // For create mode - work with local state instead of API
  initialLinks?: WorkflowLinks;
  onLinksChange?: (links: WorkflowLinks) => void;
  // Default target key from startnew config - pre-populates link key input
  defaultTargetKey?: string;
  /** This workflow's tasks, for picking the values a link passes on. */
  tasks?: PanelTask[];
}

interface LinkEntry {
  key: string;
  target: string;
  attributeMapping?: Record<string, string>;
  showSummary?: boolean;
  showPreCompleteSummary?: boolean;
  isNew?: boolean;
}

interface AttributeMappingEntry {
  key: string;
  value: string;
}

export const LinksManager = ({
  isOpen,
  onClose,
  chatbotCode,
  chatbotTitle,
  orgId,
  initialLinks,
  onLinksChange,
  defaultTargetKey,
  tasks = [],
}: LinksManagerProps) => {
  const toast = useToast();
  // A link runs once the workflow is finished, so every task can be read.
  const fieldDefsByDataset = useFieldDefinitionsForDatasets(orgId, tasks.map((task) => task.dataset ?? ''));
  const valueRefTasks = buildValueRefTasks(tasks, fieldDefsByDataset);

  // Determine if we're in create mode (no chatbotCode, using local state)
  const isCreateMode = !chatbotCode && onLinksChange !== undefined;

  // Fetch current links (only in edit mode)
  const { data: linksData, isLoading: isLoadingLinks } = useChatbotLinks(chatbotCode || '');

  // Fetch all chatbots for target selection
  const { data: chatbotsData } = useChatbots({ orgId, active_only: true });

  // Mutations
  const updateLinksMutation = useUpdateChatbotLinks();

  // Local state for editing - initialized from server data
  const [localLinks, setLocalLinks] = useState<LinkEntry[] | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [error, setError] = useState('');

  // Attribute mapping editor state
  const [editingLinkKey, setEditingLinkKey] = useState<string | null>(null);
  const [attrMappingEntries, setAttrMappingEntries] = useState<AttributeMappingEntry[]>([]);
  const [newAttrKey, setNewAttrKey] = useState('');
  const [newAttrValue, setNewAttrValue] = useState('');

  // State for default key target (separate from custom key input)
  const [defaultKeyTarget, setDefaultKeyTarget] = useState('');

  // Reset local state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLocalLinks(null);
      setNewKey('');
      setNewTarget('');
      setDefaultKeyTarget('');
      setError('');
    }
  }, [isOpen]);

  // Check if defaultTargetKey should be shown (exists and not already in links)
  const showDefaultKeyRow = useMemo(() => {
    if (!defaultTargetKey) return false;
    const existingKeys = localLinks !== null
      ? localLinks.map(l => l.key)
      : isCreateMode && initialLinks
        ? Object.keys(initialLinks)
        : linksData?.links
          ? Object.keys(linksData.links)
          : [];
    return !existingKeys.includes(defaultTargetKey);
  }, [defaultTargetKey, localLinks, isCreateMode, initialLinks, linksData]);

  // Parse WorkflowLinkValue to LinkEntry
  const parseLinkValue = (key: string, value: WorkflowLinkValue): LinkEntry => {
    const target = getTargetFromLink(value);
    const attributeMapping = getAttributeMappingFromLink(value);
    const showSummary = getShowSummaryFromLink(value);
    const showPreCompleteSummary = getShowPreCompleteSummaryFromLink(value);
    return { key, target, attributeMapping, showSummary, showPreCompleteSummary };
  };

  // Derive links from server data, initial links (create mode), or local edits
  const links = useMemo(() => {
    if (localLinks !== null) {
      return localLinks;
    }
    // In create mode, use initialLinks
    if (isCreateMode && initialLinks) {
      return Object.entries(initialLinks).map(([key, value]) =>
        parseLinkValue(key, value)
      );
    }
    // In edit mode, use server data
    if (linksData?.links) {
      return Object.entries(linksData.links).map(([key, value]) =>
        parseLinkValue(key, value as WorkflowLinkValue)
      );
    }
    return [];
  }, [linksData, localLinks, isCreateMode, initialLinks]);

  // Update local links
  const setLinks = (newLinks: LinkEntry[]) => {
    setLocalLinks(newLinks);
  };

  // Available chatbots for target dropdown (only form templates, includes self)
  const availableChatbots = chatbotsData?.items?.filter(
    (c: Chatbot) => c.template === 'form'
  ) || [];

  // Add new link entry
  const handleAddLink = () => {
    if (!newKey.trim()) {
      setError('Link key is required');
      return;
    }
    if (!newTarget) {
      setError('Target workflow is required');
      return;
    }
    if (links.some(l => l.key === newKey.trim())) {
      setError(`Link key "${newKey}" already exists`);
      return;
    }

    setLinks([...links, { key: newKey.trim(), target: newTarget, isNew: true }]);
    setNewKey('');
    setNewTarget('');
    setError('');
  };

  // Add default key link
  const handleAddDefaultKeyLink = () => {
    if (!defaultTargetKey || !defaultKeyTarget) {
      setError('Target workflow is required');
      return;
    }
    if (links.some(l => l.key === defaultTargetKey)) {
      setError(`Link key "${defaultTargetKey}" already exists`);
      return;
    }

    setLinks([...links, { key: defaultTargetKey, target: defaultKeyTarget, isNew: true }]);
    setDefaultKeyTarget('');
    setError('');
  };

  // Remove link entry
  const handleRemoveLink = (key: string) => {
    setLinks(links.filter(l => l.key !== key));
  };

  // Update link target
  const handleUpdateTarget = (key: string, target: string) => {
    setLinks(links.map(l => l.key === key ? { ...l, target } : l));
  };

  // Toggle show summary
  const handleToggleShowSummary = (key: string) => {
    setLinks(links.map(l => l.key === key ? { ...l, showSummary: !l.showSummary } : l));
  };

  // Toggle show pre-complete summary
  const handleToggleShowPreCompleteSummary = (key: string) => {
    setLinks(links.map(l => l.key === key ? { ...l, showPreCompleteSummary: !l.showPreCompleteSummary } : l));
  };

  // Open attribute mapping editor
  const handleOpenAttrEditor = (linkKey: string) => {
    const link = links.find(l => l.key === linkKey);
    if (link) {
      const entries = link.attributeMapping
        ? Object.entries(link.attributeMapping).map(([key, value]) => ({ key, value }))
        : [];
      setAttrMappingEntries(entries);
      setEditingLinkKey(linkKey);
      setNewAttrKey('');
      setNewAttrValue('');
    }
  };

  // Close attribute mapping editor
  const handleCloseAttrEditor = () => {
    setEditingLinkKey(null);
    setAttrMappingEntries([]);
    setNewAttrKey('');
    setNewAttrValue('');
  };

  // Save attribute mapping
  const handleSaveAttrMapping = () => {
    if (editingLinkKey) {
      const newMapping: Record<string, string> = {};
      attrMappingEntries.forEach(entry => {
        if (entry.key.trim() && entry.value.trim()) {
          newMapping[entry.key.trim()] = entry.value.trim();
        }
      });
      setLinks(links.map(l =>
        l.key === editingLinkKey
          ? { ...l, attributeMapping: Object.keys(newMapping).length > 0 ? newMapping : undefined }
          : l
      ));
      handleCloseAttrEditor();
    }
  };

  // Add attribute mapping entry
  const handleAddAttrEntry = () => {
    if (!newAttrKey.trim()) return;
    if (attrMappingEntries.some(e => e.key === newAttrKey.trim())) {
      return; // Duplicate key
    }
    setAttrMappingEntries([...attrMappingEntries, { key: newAttrKey.trim(), value: newAttrValue.trim() }]);
    setNewAttrKey('');
    setNewAttrValue('');
  };

  // Remove attribute mapping entry
  const handleRemoveAttrEntry = (key: string) => {
    setAttrMappingEntries(attrMappingEntries.filter(e => e.key !== key));
  };

  // Update attribute mapping entry
  const handleUpdateAttrEntry = (oldKey: string, newKey: string, newValue: string) => {
    setAttrMappingEntries(attrMappingEntries.map(e =>
      e.key === oldKey ? { key: newKey, value: newValue } : e
    ));
  };

  // Save all changes
  const handleSave = async () => {
    setError('');

    // Convert to WorkflowLinks object
    // Use extended format if any optional field is set, otherwise use simple string
    const linksObj: WorkflowLinks = {};
    links.forEach(l => {
      const hasAttributeMapping = l.attributeMapping && Object.keys(l.attributeMapping).length > 0;
      const hasShowSummary = l.showSummary === true;
      const hasShowPreCompleteSummary = l.showPreCompleteSummary === true;

      if (hasAttributeMapping || hasShowSummary || hasShowPreCompleteSummary) {
        // Extended format
        linksObj[l.key] = {
          target: l.target,
          ...(hasAttributeMapping && { attribute_mapping: l.attributeMapping }),
          ...(hasShowSummary && { show_summary: true }),
          ...(hasShowPreCompleteSummary && { show_pre_complete_summary: true }),
        };
      } else {
        // Simple format
        linksObj[l.key] = l.target;
      }
    });

    // Create mode: call onLinksChange callback
    if (isCreateMode && onLinksChange) {
      onLinksChange(linksObj);
      toast({
        title: 'Links Configured',
        description: 'Workflow links will be saved when the workflow is created.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      setLocalLinks(null);
      onClose();
      return;
    }

    // Edit mode: save to server
    try {
      await updateLinksMutation.mutateAsync({
        code: chatbotCode!,
        links: linksObj,
      });

      toast({
        title: 'Links Updated',
        description: 'Workflow links have been saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset local state so next open fetches fresh data
      setLocalLinks(null);
      onClose();
    } catch (err) {
      const axiosError = err as AxiosError<{ data?: { message?: string } }>;
      const errorMessage = axiosError?.response?.data?.data?.message || axiosError?.message || 'Failed to update links';
      setError(errorMessage);
    }
  };

  const isLoading = updateLinksMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Linked Workflows - ${chatbotTitle}`}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSave}
            isLoading={isLoading}
            loadingText="Saving"
          >
            Save Changes
          </Button>
        </>
      }
    >
      <VStack spacing={6} align="stretch">
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Box>
          <Text fontSize="sm" color="text-secondary" mb={4}>
            Configure links to other workflows. These links can be used for dynamic STARTNEW
            targeting where the target workflow is determined at runtime based on user choices.
          </Text>
        </Box>

        {/* Current Links Table */}
        {links.length > 0 && (
          <Box
            borderWidth="1px"
            borderColor="border-default"
            borderRadius="lg"
            overflow="hidden"
          >
            <Table variant="simple" size="sm">
              <Thead bg="bg-muted">
                <Tr>
                  <Th color="text-primary" borderColor="border-default">Link Key</Th>
                  <Th color="text-primary" borderColor="border-default">Target Workflow</Th>
                  <Th color="text-primary" borderColor="border-default">Attributes</Th>
                  <Th color="text-primary" borderColor="border-default" width="100px">Summary</Th>
                  <Th color="text-primary" borderColor="border-default" width="110px">Pre-fill Summary</Th>
                  <Th color="text-primary" borderColor="border-default" width="60px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {links.map((link) => (
                  <Tr key={link.key}>
                    <Td borderColor="border-default">
                      <Text fontFamily="mono" fontSize="sm">{link.key}</Text>
                    </Td>
                    <Td borderColor="border-default">
                      <Select
                        size="sm"
                        value={link.target}
                        onChange={(e) => handleUpdateTarget(link.key, e.target.value)}
                        bg="bg-surface"
                        borderColor="border-default"
                      >
                        {availableChatbots.map((c: Chatbot) => (
                          <option key={c.short_code} value={c.short_code}>
                            {c.title} ({c.short_code})
                          </option>
                        ))}
                        {/* Keep current value if not in list */}
                        {!availableChatbots.some((c: Chatbot) => c.short_code === link.target) && (
                          <option value={link.target}>{link.target}</option>
                        )}
                      </Select>
                      {!availableChatbots.some((c: Chatbot) => c.short_code === link.target) && (
                        <Text fontSize="xs" color="orange.500" mt={1}>
                          "{link.target}" is not a current workflow — it may have been renamed or deleted.
                        </Text>
                      )}
                    </Td>
                    <Td borderColor="border-default">
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="text-secondary">
                          {link.attributeMapping ? `${Object.keys(link.attributeMapping).length} attr` : 'None'}
                        </Text>
                        <IconButton
                          aria-label="Edit attributes"
                          icon={<MdEdit />}
                          size="xs"
                          variant="ghost"
                          onClick={() => handleOpenAttrEditor(link.key)}
                        />
                      </HStack>
                    </Td>
                    <Td borderColor="border-default">
                      <Tooltip
                        label={link.showSummary ? 'Show form summary before starting linked workflow' : 'Skip summary and start linked workflow immediately'}
                        placement="top"
                        hasArrow
                      >
                        <Box>
                          <Switch
                            size="sm"
                            isChecked={link.showSummary ?? false}
                            onChange={() => handleToggleShowSummary(link.key)}
                            colorScheme="brand"
                          />
                        </Box>
                      </Tooltip>
                    </Td>
                    <Td borderColor="border-default">
                      <Tooltip
                        label={link.showPreCompleteSummary ? 'Show pre-filled task recap at the top of the first task in the linked workflow' : 'Do not show pre-fill recap in linked workflow'}
                        placement="top"
                        hasArrow
                      >
                        <Box>
                          <Switch
                            size="sm"
                            isChecked={link.showPreCompleteSummary ?? false}
                            onChange={() => handleToggleShowPreCompleteSummary(link.key)}
                            colorScheme="brand"
                          />
                        </Box>
                      </Tooltip>
                    </Td>
                    <Td borderColor="border-default">
                      <IconButton
                        aria-label="Remove link"
                        icon={<MdDelete />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleRemoveLink(link.key)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {links.length === 0 && !isLoadingLinks && (
          <Box
            p={8}
            textAlign="center"
            borderWidth="1px"
            borderColor="border-default"
            borderRadius="lg"
            borderStyle="dashed"
          >
            <Text color="text-secondary">
              No workflow links configured. Add a link below to get started.
            </Text>
          </Box>
        )}

        {/* Add New Link Form */}
        <Box
          p={4}
          borderWidth="1px"
          borderColor="border-default"
          borderRadius="lg"
          bg="bg-muted"
        >
          <Text fontWeight="medium" mb={3}>Add New Link</Text>
          <VStack spacing={3} align="stretch">
            {/* Default Key Row - shown when defaultTargetKey is set and not in links */}
            {showDefaultKeyRow && (
              <HStack spacing={3} align="end">
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">Link Key (Default)</FormLabel>
                  <Input
                    size="sm"
                    value={defaultTargetKey}
                    isReadOnly
                    bg="bg-surface"
                    borderColor="brand.300"
                    fontWeight="medium"
                  />
                </FormControl>
                <FormControl flex={2}>
                  <FormLabel fontSize="sm">Target Workflow</FormLabel>
                  <Select
                    size="sm"
                    placeholder="Select target workflow"
                    value={defaultKeyTarget}
                    onChange={(e) => setDefaultKeyTarget(e.target.value)}
                    bg="bg-surface"
                    borderColor="border-default"
                  >
                    {availableChatbots.map((c: Chatbot) => (
                      <option key={c.short_code} value={c.short_code}>
                        {c.title} ({c.short_code})
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  aria-label="Add default link"
                  icon={<MdAdd />}
                  colorScheme="brand"
                  size="sm"
                  onClick={handleAddDefaultKeyLink}
                />
              </HStack>
            )}

            {/* Custom Key Row */}
            <HStack spacing={3} align="end">
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Link Key</FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g., ORDER, PAYMENT"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value.toUpperCase())}
                  bg="bg-surface"
                  borderColor="border-default"
                />
              </FormControl>
              <FormControl flex={2}>
                <FormLabel fontSize="sm">Target Workflow</FormLabel>
                <Select
                  size="sm"
                  placeholder="Select target workflow"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  bg="bg-surface"
                  borderColor="border-default"
                >
                  {availableChatbots.map((c: Chatbot) => (
                    <option key={c.short_code} value={c.short_code}>
                      {c.title} ({c.short_code})
                    </option>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                aria-label="Add link"
                icon={<MdAdd />}
                colorScheme="brand"
                size="sm"
                onClick={handleAddLink}
              />
            </HStack>
          </VStack>
        </Box>

        {/* Help Text */}
        <Box>
          <Text fontSize="xs" color="text-muted">
            Link keys are used in task configurations to specify dynamic targets.
            For example, a choice task can map its selected option to a link key,
            which is then resolved to the target workflow at runtime.
          </Text>
        </Box>
      </VStack>

      {/* Attribute Mapping Editor Modal */}
      <Modal
        isOpen={editingLinkKey !== null}
        onClose={handleCloseAttrEditor}
        title={`Attribute Mapping - ${editingLinkKey}`}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={handleCloseAttrEditor}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleSaveAttrMapping}>
              Save Attributes
            </Button>
          </>
        }
      >
        <VStack spacing={4} align="stretch">
          <Text fontSize="sm" color="text-secondary">
            Configure attributes to pass to the target workflow, such as answers from this one.
          </Text>

          {/* Current Mappings */}
          {attrMappingEntries.length > 0 && (
            <Box
              borderWidth="1px"
              borderColor="border-default"
              borderRadius="lg"
              overflow="hidden"
            >
              <Table variant="simple" size="sm">
                <Thead bg="bg-muted">
                  <Tr>
                    <Th color="text-primary" borderColor="border-default">Attribute Name</Th>
                    <Th color="text-primary" borderColor="border-default">Value</Th>
                    <Th color="text-primary" borderColor="border-default" width="60px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {attrMappingEntries.map((entry, index) => (
                    <Tr key={entry.key}>
                      <Td borderColor="border-default">
                        <Input
                          size="sm"
                          value={entry.key}
                          onChange={(e) => handleUpdateAttrEntry(entry.key, e.target.value, entry.value)}
                          bg="bg-surface"
                          borderColor="border-default"
                        />
                      </Td>
                      <Td borderColor="border-default">
                        <ValueRefPicker
                          value={entry.value}
                          onChange={(value) => handleUpdateAttrEntry(entry.key, entry.key, value)}
                          tasks={valueRefTasks}
                          testId={`wfv4-links-attr-value-${index}`}
                        />
                      </Td>
                      <Td borderColor="border-default">
                        <IconButton
                          aria-label="Remove attribute"
                          icon={<MdDelete />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleRemoveAttrEntry(entry.key)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {attrMappingEntries.length === 0 && (
            <Box
              p={4}
              textAlign="center"
              borderWidth="1px"
              borderColor="border-default"
              borderRadius="lg"
              borderStyle="dashed"
            >
              <Text fontSize="sm" color="text-secondary">
                No attributes configured. Add attributes below to pass data to the target workflow.
              </Text>
            </Box>
          )}

          {/* Add New Attribute */}
          <Box
            p={4}
            borderWidth="1px"
            borderColor="border-default"
            borderRadius="lg"
            bg="bg-muted"
          >
            <Text fontWeight="medium" mb={3} fontSize="sm">Add Attribute</Text>
            <HStack spacing={3} align="end">
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g., store_code"
                  value={newAttrKey}
                  onChange={(e) => setNewAttrKey(e.target.value)}
                  bg="bg-surface"
                  borderColor="border-default"
                />
              </FormControl>
              <FormControl flex={2}>
                <FormLabel fontSize="sm">Value</FormLabel>
                <ValueRefPicker
                  value={newAttrValue}
                  onChange={setNewAttrValue}
                  tasks={valueRefTasks}
                  testId="wfv4-links-attr-new-value"
                />
              </FormControl>
              <IconButton
                aria-label="Add attribute"
                icon={<MdAdd />}
                colorScheme="brand"
                size="sm"
                onClick={handleAddAttrEntry}
              />
            </HStack>
          </Box>

        </VStack>
      </Modal>
    </Modal>
  );
};
